import React from 'react';
import { Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ResourceColumn, ResourceListPage } from '../../components/resource/ResourceListPage';
import { DiaTrabalho, DiaTrabalhoCardapioEntry, SituacaoProducaoDiaTrabalho } from '../../types/producao';
import { diaTrabalhoService } from '../../services';

const situacaoLabels: Record<SituacaoProducaoDiaTrabalho, string> = {
  ABERTO: 'Aberto',
  PRODUZIDO: 'Produzido',
  CANCELADO: 'Cancelado',
};

const situacaoBadge: Record<SituacaoProducaoDiaTrabalho, string> = {
  ABERTO: 'warning',
  PRODUZIDO: 'success',
  CANCELADO: 'secondary',
};

const columns: ResourceColumn<DiaTrabalho>[] = [
  { header: 'Data', accessor: 'data' },
  { header: 'Responsável', accessor: 'responsavel_nome' },
  {
    header: 'Produções',
    render: (item) => String(item.producoes?.length ?? 0),
  },
  {
    header: 'Situações',
    render: (item) => {
      const situacoes = Array.from(
        new Set<string>(
          (item.producoes ?? []).map((prod) => prod.situacao ?? '').filter(Boolean),
        ),
      ) as SituacaoProducaoDiaTrabalho[];
      if (situacoes.length === 0) {
        return '—';
      }
      return (
        <>
          {situacoes.map((situacao) => (
            <Badge key={situacao} bg={situacaoBadge[situacao]} className="me-1">
              {situacaoLabels[situacao] ?? situacao}
            </Badge>
          ))}
        </>
      );
    },
  },
  { header: 'Atualizado', accessor: 'updated_at' },
];

const renderCardapioEntry = (entry: DiaTrabalhoCardapioEntry) => (
  <Card key={entry.ficha_id} className="mb-3">
    <Card.Body>
      <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-2">
        <div>
          <h6 className="mb-1">
            {entry.codigo ?? `Ficha #${entry.ficha_id}`} {entry.titulo}
          </h6>
          <p className="text-muted mb-0">
            Produto final: {entry.produto_final?.nome ?? entry.produto_final?.codigo ?? '—'}
          </p>
        </div>
        <div className="text-md-end">
          <div>
            <strong>Qtd total:</strong> {entry.quantidade_total ?? '0'}
          </div>
          <div className="text-muted small">
            Produzido: {entry.quantidade_produzida ?? '0'}
          </div>
        </div>
      </div>

    <div className="d-flex flex-wrap gap-2 mb-2">
        {(entry.situacoes ?? []).map((situacao) => {
          const situacaoKey = situacao as SituacaoProducaoDiaTrabalho;
          return (
            <Badge key={`${entry.ficha_id}-${situacao}`} bg={situacaoBadge[situacaoKey] ?? 'secondary'}>
              {situacaoLabels[situacaoKey] ?? situacao}
            </Badge>
          );
        })}
      </div>

      <div className="d-flex flex-wrap gap-4 mb-2 text-muted small">
        {entry.rendimento && (
          <div>
            <strong>Rendimento:</strong> {entry.rendimento.quantidade ?? '—'} {entry.rendimento.unidade ?? ''}
          </div>
        )}
        {entry.tempo_total_min != null && (
          <div>
            <strong>Tempo total:</strong> {entry.tempo_total_min} min
          </div>
        )}
        {entry.tempo_setup_min != null && (
          <div>
            <strong>Setup:</strong> {entry.tempo_setup_min} min
          </div>
        )}
      </div>

      {entry.subprocessos.length > 0 && (
        <Table size="sm" responsive className="mb-0">
          <thead>
            <tr>
              <th>Seq</th>
              <th>Subficha</th>
              <th>Qtd</th>
              <th>Unidade</th>
            </tr>
          </thead>
          <tbody>
            {entry.subprocessos.map((sub) => (
              <tr key={sub.id}>
                <td>{sub.sequencia}</td>
                <td>{sub.codigo ?? sub.titulo ?? '—'}</td>
                <td>{sub.quantidade}</td>
                <td>{sub.unidade ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card.Body>
  </Card>
);

export const DiaTrabalhoListPage: React.FC = () => {
  const navigate = useNavigate();

  const renderRowActions = (record: DiaTrabalho) => (
    <Button
      variant="outline-info"
      size="sm"
      disabled={!record.id}
      onClick={() => record.id && navigate(`/admin/producao/dias-trabalho/${record.id}/relatorio`)}
    >
      Ficha técnica
    </Button>
  );

  return (
    <>
      <ResourceListPage
        title="Dias de trabalho"
        description="Planeje e acompanhe o cardápio diário de produção."
        columns={columns}
        service={diaTrabalhoService}
        createPath="/admin/producao/dias-trabalho/novo"
        onEdit={(record) => record.id && navigate(`/admin/producao/dias-trabalho/${record.id}`)}
        renderRowActions={renderRowActions}
        searchPlaceholder="Buscar por responsável..."
      />

      {/* Report page replaced modal: navegates to dedicated route */}
    </>
  );
};
